/**
 * Display groupings for API/`Module` names (as they appear on the wire) used
 * by the consumption breakdown chart. Display labels live in `copy.tsx` under
 * `components.sduDashboard.groupLabels`. Per-group colors are defined as CSS
 * custom properties in `src/ui/styles/components/sdu-dashboard.scss` and read
 * at runtime via `readGroupColors`.
 *
 * Modules not assigned to any group are dropped from the breakdown chart;
 * their consumption is still counted in the all-time total tile.
 */

export const MODULE_GROUPS = [
  {
    key: 'dataModelingAndManagement',
    modules: ['Designer', 'Knowledge Catalog'],
  },
  {
    key: 'naturalLanguageInteractions',
    modules: ['Voicebox'],
  },
  {
    key: 'queryAndGraphExploration',
    modules: ['Studio', 'Explorer'],
  },
  {
    key: 'documentProcessing',
    modules: ['Bites'],
  },
  {
    key: 'programmaticAccess',
    modules: ['Stardog Internal'],
  },
] as const;

export type ModuleGroupKey = typeof MODULE_GROUPS[number]['key'];

export const GROUP_KEYS: readonly ModuleGroupKey[] = MODULE_GROUPS.map(
  (g) => g.key
);

const MODULE_TO_GROUP: Record<string, ModuleGroupKey> = MODULE_GROUPS.reduce(
  (acc, group) => {
    group.modules.forEach((m) => {
      acc[m] = group.key;
    });
    return acc;
  },
  {} as Record<string, ModuleGroupKey>
);

export const groupForModule = (module: string): ModuleGroupKey | null =>
  MODULE_TO_GROUP[module] ?? null;

const moduleGroupSlug = (key: ModuleGroupKey): string =>
  key.replace(/([A-Z])/g, '-$1').toLowerCase();

export const moduleGroupCssVar = (key: ModuleGroupKey): string =>
  `--sdu-group-${moduleGroupSlug(key)}`;

export const readGroupColors = (): Record<ModuleGroupKey, string> => {
  const empty = GROUP_KEYS.reduce((acc, k) => {
    acc[k] = '';
    return acc;
  }, {} as Record<ModuleGroupKey, string>);

  if (typeof window === 'undefined') {
    return empty;
  }

  const styles = getComputedStyle(document.documentElement);
  return GROUP_KEYS.reduce((acc, k) => {
    acc[k] = styles.getPropertyValue(moduleGroupCssVar(k)).trim();
    return acc;
  }, {} as Record<ModuleGroupKey, string>);
};
